import { Color3, PhysicsMotionType, PhysicsShapeType, Vector2, Vector3 } from '@babylonjs/core';
import { Asset, AssetType } from '../Asset';
import { AssetObject } from '../AssetObject';
import { DecorationConfig } from '../DecorationGenerator';
import { EnemyConfig } from '../EnemyGenerator';
import { EnemyPrefabs } from '../EnemyPrefabs';
import { FogConfig } from '../FogConfig';
import { Level } from '../Level';
import {
  CameraConfig,
  CollectibleDistributionConfig,
  LevelConfig,
  MaterialConfig,
  PathConfig,
  PathLightsConfig,
  PlayerConfig,
  PostProcessConfig,
  SoundConfig,
  WaterConfig,
  WayGenerationConfig,
} from '../LevelConfig';
import { LightConfig } from '../LightConfig';
import { HeightMapConfig, SkyBoxConfig } from '../MeshFactory';
import { ObstacleConfig } from '../ObstacleGenerator';
import { ObstaclePrefabs } from '../ObstaclePrefabs';
import { TextureAtlas } from '../TextureAtlas';
import { LevelTemplate } from './LevelTemplate';

export class TemplateCave implements LevelTemplate {
  create(): LevelConfig {
    const skyboxConfig: SkyBoxConfig = {
      url: 'assets/skybox/skybox_16.jpg',
    };
    const fogConfig = new FogConfig();
    fogConfig.color = new Color3(1, 0.75, 0.75);
    const lightConfig = new LightConfig();
    lightConfig.intensity /= 4;
    lightConfig.hemiIntensity /= 2;
    lightConfig.hemiGroundColor = new Color3(1, 1, 1);
    lightConfig.diffuse = fogConfig.color;
    lightConfig.lensflare = false;

    const postProcessConfig: PostProcessConfig = {
      fxaa: false,
      motionBlur: 0,
      //blur: { kernel: 32, strength: 1, x: 1, y: 0 },
      highlights: false,
      sepia: false,
      fireflies: 1,
    };

    const materialConfig: MaterialConfig = {
      colorMaterialBaseColor: new Color3(0.5, 0.7, 0.9),
      colorMaterialCount: 10,
      textures: [
        { url: 'assets/textures/dirt-ground2.avif', uScale: 1, vScale: 1 },
        { url: 'assets/textures/dirt-ground.avif', uScale: 4, vScale: 0.5 },
        { url: 'assets/textures/green-grass.jpg', uScale: 4, vScale: 0.5 },
        { url: 'assets/textures/stone-ground.jpg', uScale: 2, vScale: 2 },
        { url: 'assets/textures/stone-ground2.jpg', uScale: 2, vScale: 2 },
        { url: 'assets/textures/stone-ground-colored.jpg', uScale: 3, vScale: 3 },
        { url: 'assets/textures/wood-ground.jpg', uScale: 2, vScale: 0.3 },
      ],
      globalTextureNames: [],
      materials: [],
    };

    const wayConfig: WayGenerationConfig = {
      width: 9,
      height: 1,
      //shape: PathFactory.shapeHalfpipe(),
      segmentsAcross: 12,
      yOffset: 0,
      material: 'dirt-ground',
      sideMaterial: 'dirt-ground2',
      gapProbability: 0,
      gapSize: { min: 1, max: 2 },
    };

    const pathConfig: PathConfig = {
      start: new Vector3(-240, 0, -240),
      destination: new Vector3(240, 0, 240),
      pathPoints: 8,
      segmentsPerCurve: 10,
      pathCurvature: 1.5,
      pathHilliness: 0.0,
      pathScale: 3.0,
      pathMinCurveAngle: 90,
      laneWidth: 3,
      spaceAround: 50,
    };

    const pathLightConfig: PathLightsConfig = {
      interval: 10,
      colors: [new Color3(1, 0, 0)],
      spot: false,
      intensity: 0,
      offset: new Vector3(0, 5, 0),
    };

    const soundConfig: SoundConfig = {
      backgroundMusic: 'sunnySkies',
      backgroundMusicVolume: 1.0,
    };

    const cameraConfig: CameraConfig = {
      alignToPath: false,
      distance: 8,
      heightOffset: 1.2,
      smoothing: 40,
      alphaSmoothing: 1,
      collision: false,
      orthographic: false,
      orthographicZoom: 8,
      orthographicAlpha: Math.PI / 10,
      orthographicBeta: Math.PI / 3,
    };

    const collectibleConfig: CollectibleDistributionConfig = {
      offset: new Vector3(0, 1, 0),
      probability: 0.1,
      minPoint: 10,
      mixPatternTypes: false,
      maxPatternSize: 3,
      spawnRange: 60,
      despawnRange: 90,
    };

    const waterConfig: WaterConfig = {
      level: -1,
      depth: 50,
      size: 2000,
      effect: true,
      mesh: true,
      renderTargetSize: new Vector2(512, 512),
      bumpTextureUrl: 'assets/textures/waterbump2.png',
      bumpScale: 10,
      windForce: 0.5,
      waveHeight: 0.1,
      waveLength: 0.1,
      bumpHeight: 0.1,
      windDirection: new Vector2(1, 1),
      waterColor: new Color3(221 / 255, 0, 0),
      colorBlendFactor: 0.2,
      alpha: 0.75,
    };

    const wayHalfWidth = wayConfig.width / 2;

    const decorationConfig: DecorationConfig = {
      assets: [
        new Asset('rubble-half', AssetType.MESH, 'assets/model/dungeon_shiny/', 'rubble_half.gltf'),
        new Asset('rubble-large', AssetType.MESH, 'assets/model/dungeon_shiny/', 'rubble_large.gltf'),
        new Asset('Gold_Nuggets', AssetType.MESH, 'assets/model/resources/', 'Gold_Nuggets.gltf'),
        new Asset('TreeA', AssetType.MESH, 'assets/model/forest/', 'Tree_Bare_1_A_Color1.gltf'),
        new Asset('TreeB', AssetType.MESH, 'assets/model/forest/', 'Tree_Bare_1_B_Color1.gltf'),
        new Asset('TreeC', AssetType.MESH, 'assets/model/forest/', 'Tree_Bare_1_C_Color1.gltf'),
        new Asset('TreeD', AssetType.MESH, 'assets/model/forest/', 'Tree_Bare_2_A_Color1.gltf'),
        new Asset('TreeE', AssetType.MESH, 'assets/model/forest/', 'Tree_Bare_2_B_Color1.gltf'),
        new Asset('TreeF', AssetType.MESH, 'assets/model/forest/', 'Tree_Bare_2_C_Color1.gltf'),
      ],
      assetObjects: [
        new AssetObject('rubble-large').withType('rubble'),
        new AssetObject('rubble-half').withType('rubble'),
        new AssetObject('TreeA').withType('trees'),
        new AssetObject('TreeB').withType('trees'),
        new AssetObject('TreeC').withType('trees'),
        new AssetObject('TreeD').withType('trees'),
        new AssetObject('TreeE').withType('trees'),
        new AssetObject('TreeF').withType('trees'),
        new AssetObject('Gold_Nuggets').withType('gold'),
      ],
      distributions: [
        {
          types: ['rubble'],
          mode: 'zone',
          zone: { min: -wayConfig.width, max: wayConfig.width },
          deadZone: { min: -wayConfig.width / 1.5, max: wayConfig.width / 1.5 },
          density: 1,
          interval: 1,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 0.5, max: 1 },
          skipProbability: 0.2,
          skipOccupied: true,
          useLevelYPosition: true,
          rolling: true,
        },

        {
          types: ['gold'],
          mode: 'zone',
          zone: { min: -wayHalfWidth * 1.7, max: wayHalfWidth * 1.9 },
          deadZone: { min: -wayHalfWidth * 1.3, max: wayHalfWidth * 1.3 },
          density: 1,
          alignToFace: true,
          rolling: true,
          interval: 2,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 5, max: 6 },
          skipProbability: 0.2,
          useLevelYPosition: true,
        },

        {
          types: ['trees'],
          mode: 'zone',
          zone: { min: -wayHalfWidth * 1.5, max: wayHalfWidth * 2 },
          deadZone: { min: -wayHalfWidth * 1.2, max: wayHalfWidth * 1.2 },
          density: 2,
          alignToFace: true,
          interval: 1,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 1, max: 1.5 },
          rolling: true,
          skipProbability: 0.2,
          useLevelYPosition: true,
        },
      ],
    };

    const minPoint = 10;
    const obstacleConfig: ObstacleConfig = ObstaclePrefabs.create({
      probability: 0.01,
      minPoint: minPoint,
      pathWidth: 10,
      spawnRange: 60,
      despawnRange: 90,
      obstacleColor: 'yellow',
      excludes: ['swiper-quad', 'chest'],
    });

    const enemyConfig: EnemyConfig = EnemyPrefabs.create({
      probability: 0.01,
      minPoint: minPoint,
      pathWidth: 10,
      spawnRange: 60,
      despawnRange: 90,
    });

    // grounds and ceilings matrices should match on their edges, ensure this
    const groundWidth = 500;
    const groundHeight = 500;
    const groundSubdivisions = 50;
    const centerOnPath = false;
    const heightMapConfig = HeightMapConfig.builder()
      .name('heightmap-ground')
      .heightMapImageUrl('assets/heightmap/ambientocclusion.jpg')
      .width(groundWidth)
      .height(groundHeight)
      .updatable(true)
      .subdivisions(groundSubdivisions)
      .castShadows(false)
      .maxHeight(50)
      .minHeight(0)
      .metadata({
        centerOnPath,
      })
      .pathWidth(30) // width of the corridor
      .pathTransitionWidth(7) // steepness of the walls (bigger = softer)
      .pathYOffset(4) // optional: Y-offset
      .terrainTextureAtlas('cartoon1')
      .terrainTextureName1('Marble')
      .terrainTextureName2('Magma')
      .terrainTextureName3('Dirt')
      .terrainTextureScale(new Vector3(150, 150, 150))
      .backFaceCulling(false)
      .physicsShapeType(PhysicsShapeType.MESH)
      .physicsMotionType(PhysicsMotionType.ANIMATED)
      .position(new Vector3(0, -5, 0))
      .build();

    const ceilingConfig = HeightMapConfig.builder()
      .name('ceiling')
      .heightMapImageUrl('assets/heightmap/ambientocclusion_inverted.jpg')
      .width(groundWidth)
      .height(groundHeight)
      .updatable(true)
      .subdivisions(groundSubdivisions)
      .castShadows(false)
      .receiveShadows(false)
      .maxHeight(50)
      .minHeight(0)
      .metadata({
        mergeEdges: true,
        centerOnPath,
      })
      .terrainTextureAtlas('cartoon1')
      .terrainTextureName1('Magma')
      .terrainTextureName2('Magma')
      .terrainTextureName3('Dirt')
      //.rotation(new Vector3(Math.PI, 0, 0)) // rotate 180 degrees
      .terrainTextureScale(new Vector3(100, 100, 100))
      .backFaceCulling(false)
      .physicsShapeType(PhysicsShapeType.MESH)
      .physicsMotionType(PhysicsMotionType.ANIMATED)
      .position(new Vector3(0, 5, 0))
      .build();

    const playerConfig: PlayerConfig = {
      startPathIndex: 4,
      startOffset: new Vector3(0, 3, 0),
      autoRun: 0,
      speed: 10,
      jumpSpeed: 11,
      backwardSpeed: 4,
      turnSpeed: 10,
      dashSpeed: 12,
      projectileSpeed: 20,
    };

    const atlasConfig = [new TextureAtlas('cartoon1', 'assets/atlas/', 'cartoon1.png', 'cartoon1.json')];

    const config = new LevelConfig(
      'Deep Cave',
      2, // seed
      100, // timelimit
      Level.fullGravity, // gravity
      -45, // killing height
      100, // height above ground
      40, // checkpoint interval
      4, // goal end offset
      playerConfig,
      cameraConfig,
      pathConfig,
      lightConfig,
      skyboxConfig,
      materialConfig
    );

    config.sound = soundConfig;
    config.fog = fogConfig;
    config.way = wayConfig;
    config.collectible = collectibleConfig;
    config.postProcess = postProcessConfig;
    config.heightMap = heightMapConfig;
    config.decoration = decorationConfig;
    config.enemy = enemyConfig;
    config.obstacle = obstacleConfig;
    config.water = waterConfig;
    config.ceiling = ceilingConfig;
    config.atlasses = atlasConfig;
    config.pathLights = pathLightConfig;

    return config;
  }
}
